import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaLock, FaLockOpen, FaSpinner } from "react-icons/fa";
import { useAuth } from "../../Authentication/AuthContext";
import Login from "../../Authentication/Login/Login";
import SubscriptionMonthly from "../../Authentication/Subscription/SubscriptionMonthly";
import "./BooksPageStyles.css";

type BookLevelKey = "A1" | "A2" | "B1" | "B2" | "C1" | "C2";

type ProtectedBook = {
  level: BookLevelKey;
  book_id: number;
  title: string;
  file_name: string;
  api_path: string;
  view_url: string;
  size_bytes?: number;
  updated_at?: string;
};

type ProtectedBookLevel = {
  level: BookLevelKey;
  title: string;
  subtitle: string;
  book_count: number;
  books: ProtectedBook[];
};

type ProtectedBooksResponse = {
  email: string;
  source?: string;
  has_record: boolean;

  manual_checked?: boolean;
  manual_has_record?: boolean;

  revenuecat_checked?: boolean;
  revenuecat_configured?: boolean;
  revenuecat_customer_ids?: string[];
  revenuecat_matched_emails?: string[];
  revenuecat_active_entitlements?: string[];
  revenuecat_active_products?: string[];
  revenuecat_subscription_ids?: string[];
  revenuecat_error?: string | null;

  has_full_access: boolean;
  has_full_course_access?: boolean;
  has_full_book_access?: boolean;

  course_access_levels: string[];
  book_access_levels?: string[];

  accessible_levels: BookLevelKey[];
  accessible_course_levels?: BookLevelKey[];
  accessible_book_levels?: BookLevelKey[];

  total_books: number;
  levels: ProtectedBookLevel[];
};

type CachedProtectedBooksResponse = {
  saved_at: number;
  data: ProtectedBooksResponse;
};

type ApiJsonResponse = {
  html?: string;
  content?: string;
  data?: {
    html?: string;
    content?: string;
  };
  message?: string;
  error?: string;
};

type PreloadedBookCache = {
  saved_at: number;
  email: string;
  level: BookLevelKey;
  book_id: number;
  title: string;
  file_name: string;
  html: string;
};

type DisplayBook = ProtectedBook & {
  is_unlocked: boolean;
};

type DisplayBookLevel = {
  level: BookLevelKey;
  title: string;
  subtitle: string;
  book_count: number;
  unlocked_count: number;
  books: DisplayBook[];
};

type BooksAccessStatus = "idle" | "checking" | "ready" | "error";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_BACKEND_API_URL ||
  "https://www.api.dearlanguage.com"
).replace(/\/+$/, "");

const BOOKS_CACHE_PREFIX = "dear-language-protected-books-v9";
const PRELOADED_BOOK_CACHE_PREFIX = "dear-language-preloaded-book-v2";
const PRELOADED_BOOK_CACHE_MAX_AGE_MS = 1000 * 60 * 30;

const OLD_BOOKS_CACHE_PREFIXES = [
  "dear-language-protected-books",
  "dear-language-protected-books-v1",
  "dear-language-protected-books-v2",
  "dear-language-protected-books-v3",
  "dear-language-protected-books-v4",
  "dear-language-protected-books-v5",
  "dear-language-protected-books-v6",
  "dear-language-protected-books-v7",
  "dear-language-protected-books-v8",
];

const BOOKS_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 12;

const BOOKS_ACCESS_REFRESH_EVENTS = [
  "dear-language-access-refresh",
  "dear-language-premium-updated",
  "dear-language-subscription-updated",
  "dear-language-books-access-refresh",
];

const ALL_BOOK_LEVELS: BookLevelKey[] = ["A1", "A2", "B1", "B2", "C1", "C2"];

const LEVEL_META: Record<BookLevelKey, { title: string; subtitle: string }> = {
  A1: {
    title: "Anfänger",
    subtitle: "",
  },
  A2: {
    title: "Grundlagen",
    subtitle: "",
  },
  B1: {
    title: "Mittelstufe",
    subtitle: "",
  },
  B2: {
    title: "Oberstufe",
    subtitle: "",
  },
  C1: {
    title: "Profi",
    subtitle: "",
  },
  C2: {
    title: "Experte",
    subtitle: "",
  },
};

const booksMemoryCache = new Map<string, CachedProtectedBooksResponse>();
const booksRequestInFlight = new Map<string, Promise<ProtectedBooksResponse>>();

const normalizePathname = (pathname: string): string => {
  const cleanPathname = String(pathname || "/")
    .trim()
    .toLowerCase();

  if (!cleanPathname || cleanPathname === "/") {
    return "/";
  }

  return cleanPathname.replace(/\/+$/, "");
};

const isBookViewPath = (path: string): boolean => {
  return normalizePathname(path.split("?")[0]) === "/book-view";
};

const prepareBookViewRoute = (): void => {
  document.documentElement.classList.remove("books-screen-page");
  document.body.classList.remove("books-screen-page");

  document.documentElement.classList.add("book-view-screen-page");
  document.body.classList.add("book-view-screen-page");

  window.dispatchEvent(new CustomEvent("dear-language-book-view-opened"));
  window.dispatchEvent(
    new CustomEvent("dear-language-route-change", {
      detail: {
        pathname: "/book-view",
        search: window.location.search,
        routeKey: "buch-ansicht",
      },
    }),
  );
};

const isBookLevel = (value: unknown): value is BookLevelKey => {
  const cleanValue = String(value || "")
    .trim()
    .toUpperCase();

  return (
    cleanValue === "A1" ||
    cleanValue === "A2" ||
    cleanValue === "B1" ||
    cleanValue === "B2" ||
    cleanValue === "C1" ||
    cleanValue === "C2"
  );
};

const normalizeBookLevel = (value: unknown): BookLevelKey | null => {
  const cleanValue = String(value || "")
    .trim()
    .toUpperCase();

  return isBookLevel(cleanValue) ? cleanValue : null;
};

const normalizeEmail = (email?: string | null): string => {
  return String(email || "")
    .trim()
    .toLowerCase();
};

const normalizeId = (value?: string | null): string => {
  return String(value || "").trim();
};

const getJsonHtml = (value: ApiJsonResponse): string => {
  return (
    value.html ||
    value.content ||
    value.data?.html ||
    value.data?.content ||
    ""
  );
};

const sortBookLevels = (levels: ProtectedBookLevel[]): ProtectedBookLevel[] => {
  const order: Record<BookLevelKey, number> = {
    A1: 1,
    A2: 2,
    B1: 3,
    B2: 4,
    C1: 5,
    C2: 6,
  };

  return [...levels].sort((a, b) => order[a.level] - order[b.level]);
};

const sortBooks = (books: ProtectedBook[]): ProtectedBook[] => {
  return [...books].sort((a, b) => a.book_id - b.book_id);
};

const createPublicBook = (
  level: BookLevelKey,
  bookId: number,
): ProtectedBook => {
  return {
    level,
    book_id: bookId,
    title: `Deutsch ${level}.${bookId}`,
    file_name: `${level}_${bookId}.html`,
    api_path: `/api/protected-books/${level}/${bookId}`,
    view_url: "",
  };
};

const createPublicBookLevels = (): ProtectedBookLevel[] => {
  return ALL_BOOK_LEVELS.map((level) => {
    const meta = LEVEL_META[level];

    const books = Array.from({ length: 10 }, (_, index) =>
      createPublicBook(level, index + 1),
    );

    return {
      level,
      title: meta.title,
      subtitle: meta.subtitle,
      book_count: books.length,
      books,
    };
  });
};

const PUBLIC_BOOK_LEVELS = createPublicBookLevels();

const getBooksCacheKey = (email: string): string => {
  return `${BOOKS_CACHE_PREFIX}:${normalizeEmail(email)}`;
};

const getPreloadedBookCacheKey = (
  email: string,
  level: BookLevelKey,
  bookId: number,
): string => {
  return `${PRELOADED_BOOK_CACHE_PREFIX}:${normalizeEmail(
    email,
  )}:${level}:${bookId}`;
};

const getLastPreloadedBookCacheKey = (
  level: BookLevelKey,
  bookId: number,
): string => {
  return `${PRELOADED_BOOK_CACHE_PREFIX}:last:${level}:${bookId}`;
};

const isCacheFresh = (cachedValue: CachedProtectedBooksResponse): boolean => {
  if (!cachedValue?.data || !cachedValue?.saved_at) {
    return false;
  }

  return Date.now() - cachedValue.saved_at <= BOOKS_CACHE_MAX_AGE_MS;
};

const isPreloadedBookFresh = (cachedValue: PreloadedBookCache): boolean => {
  if (!cachedValue?.html || !cachedValue?.saved_at) {
    return false;
  }

  return Date.now() - cachedValue.saved_at <= PRELOADED_BOOK_CACHE_MAX_AGE_MS;
};

const getCachedPreloadedBook = (
  email: string,
  level: BookLevelKey,
  bookId: number,
): PreloadedBookCache | null => {
  const cleanEmail = normalizeEmail(email);

  const keys = [
    cleanEmail ? getPreloadedBookCacheKey(cleanEmail, level, bookId) : "",
    getLastPreloadedBookCacheKey(level, bookId),
  ].filter(Boolean);

  try {
    for (const key of keys) {
      const rawValue = window.sessionStorage.getItem(key);

      if (!rawValue) {
        continue;
      }

      const parsed = JSON.parse(rawValue) as PreloadedBookCache;

      if (!isPreloadedBookFresh(parsed)) {
        window.sessionStorage.removeItem(key);
        continue;
      }

      return parsed;
    }

    return null;
  } catch {
    return null;
  }
};

const savePreloadedBook = (
  email: string,
  book: DisplayBook,
  html: string,
): void => {
  const cleanEmail = normalizeEmail(email);
  const cleanHtml = String(html || "").trim();

  if (!cleanHtml) {
    return;
  }

  try {
    const payload: PreloadedBookCache = {
      saved_at: Date.now(),
      email: cleanEmail,
      level: book.level,
      book_id: book.book_id,
      title: book.title,
      file_name: book.file_name,
      html: cleanHtml,
    };

    window.sessionStorage.setItem(
      getLastPreloadedBookCacheKey(book.level, book.book_id),
      JSON.stringify(payload),
    );

    if (cleanEmail) {
      window.sessionStorage.setItem(
        getPreloadedBookCacheKey(cleanEmail, book.level, book.book_id),
        JSON.stringify(payload),
      );
    }
  } catch {
    // Cache ist optional.
  }
};

const clearOldBookCaches = (email?: string | null): void => {
  const cleanEmail = normalizeEmail(email);

  if (!cleanEmail) {
    return;
  }

  try {
    OLD_BOOKS_CACHE_PREFIXES.forEach((prefix) => {
      window.sessionStorage.removeItem(`${prefix}:${cleanEmail}`);
      window.localStorage.removeItem(`${prefix}:${cleanEmail}`);
    });
  } catch {
    // Cache löschen ist optional.
  }
};

const normalizeProtectedBooksResponse = (
  raw: Partial<ProtectedBooksResponse> | null | undefined,
): ProtectedBooksResponse => {
  const safeRaw: Partial<ProtectedBooksResponse> = raw ?? {};
  const safeLevels = Array.isArray(safeRaw.levels) ? safeRaw.levels : [];

  const levels: ProtectedBookLevel[] = safeLevels
    .map((levelGroup): ProtectedBookLevel | null => {
      const normalizedLevel = normalizeBookLevel(levelGroup?.level);

      if (!normalizedLevel) {
        return null;
      }

      const books = Array.isArray(levelGroup?.books)
        ? levelGroup.books
            .map((book): ProtectedBook | null => {
              const bookLevel = normalizeBookLevel(book?.level);

              if (!bookLevel) {
                return null;
              }

              const bookId = Number(book?.book_id || 0);

              if (!Number.isInteger(bookId) || bookId < 1 || bookId > 10) {
                return null;
              }

              return {
                level: bookLevel,
                book_id: bookId,
                title: String(book?.title || `Deutsch ${bookLevel}.${bookId}`),
                file_name: String(
                  book?.file_name || `${bookLevel}_${bookId}.html`,
                ),
                api_path: String(
                  book?.api_path ||
                    `/api/protected-books/${bookLevel}/${bookId}`,
                ),
                view_url: String(book?.view_url || ""),
                size_bytes: Number(book?.size_bytes || 0),
                updated_at: book?.updated_at ? String(book.updated_at) : "",
              };
            })
            .filter((book): book is ProtectedBook => Boolean(book))
        : [];

      return {
        level: normalizedLevel,
        title: String(levelGroup?.title || LEVEL_META[normalizedLevel].title),
        subtitle: String(levelGroup?.subtitle || ""),
        book_count: Number(levelGroup?.book_count || books.length),
        books: sortBooks(books),
      };
    })
    .filter((level): level is ProtectedBookLevel => Boolean(level));

  const sortedLevels = sortBookLevels(levels);

  const accessibleBookLevelsSource = Array.isArray(
    safeRaw.accessible_book_levels,
  )
    ? safeRaw.accessible_book_levels
    : safeRaw.accessible_levels;

  const accessibleBookLevels = Array.isArray(accessibleBookLevelsSource)
    ? accessibleBookLevelsSource
        .map((item) => normalizeBookLevel(item))
        .filter((item): item is BookLevelKey => Boolean(item))
    : sortedLevels.map((levelGroup) => levelGroup.level);

  const accessibleCourseLevels = Array.isArray(safeRaw.accessible_course_levels)
    ? safeRaw.accessible_course_levels
        .map((item) => normalizeBookLevel(item))
        .filter((item): item is BookLevelKey => Boolean(item))
    : [];

  const totalBooks =
    Number(safeRaw.total_books || 0) ||
    sortedLevels.reduce(
      (total, levelGroup) => total + levelGroup.books.length,
      0,
    );

  return {
    email: String(safeRaw.email || ""),
    source: safeRaw.source ? String(safeRaw.source) : undefined,
    has_record: Boolean(safeRaw.has_record),

    manual_checked: Boolean(safeRaw.manual_checked),
    manual_has_record: Boolean(safeRaw.manual_has_record),

    revenuecat_checked: Boolean(safeRaw.revenuecat_checked),
    revenuecat_configured: Boolean(safeRaw.revenuecat_configured),
    revenuecat_customer_ids: Array.isArray(safeRaw.revenuecat_customer_ids)
      ? safeRaw.revenuecat_customer_ids.map((item) => String(item))
      : [],
    revenuecat_matched_emails: Array.isArray(safeRaw.revenuecat_matched_emails)
      ? safeRaw.revenuecat_matched_emails.map((item) => String(item))
      : [],
    revenuecat_active_entitlements: Array.isArray(
      safeRaw.revenuecat_active_entitlements,
    )
      ? safeRaw.revenuecat_active_entitlements.map((item) => String(item))
      : [],
    revenuecat_active_products: Array.isArray(
      safeRaw.revenuecat_active_products,
    )
      ? safeRaw.revenuecat_active_products.map((item) => String(item))
      : [],
    revenuecat_subscription_ids: Array.isArray(
      safeRaw.revenuecat_subscription_ids,
    )
      ? safeRaw.revenuecat_subscription_ids.map((item) => String(item))
      : [],
    revenuecat_error:
      typeof safeRaw.revenuecat_error === "string"
        ? safeRaw.revenuecat_error
        : null,

    has_full_access: Boolean(
      safeRaw.has_full_book_access ?? safeRaw.has_full_access,
    ),
    has_full_course_access: Boolean(safeRaw.has_full_course_access),
    has_full_book_access: Boolean(
      safeRaw.has_full_book_access ?? safeRaw.has_full_access,
    ),

    course_access_levels: Array.isArray(safeRaw.course_access_levels)
      ? safeRaw.course_access_levels.map((item) => String(item))
      : [],

    book_access_levels: Array.isArray(safeRaw.book_access_levels)
      ? safeRaw.book_access_levels.map((item) => String(item))
      : [],

    accessible_levels: accessibleBookLevels,
    accessible_course_levels: accessibleCourseLevels,
    accessible_book_levels: accessibleBookLevels,

    total_books: totalBooks,
    levels: sortedLevels,
  };
};

const getCachedBooksData = (email: string): ProtectedBooksResponse | null => {
  const cleanEmail = normalizeEmail(email);

  if (!cleanEmail) {
    return null;
  }

  const cacheKey = getBooksCacheKey(cleanEmail);
  const memoryCache = booksMemoryCache.get(cacheKey);

  if (memoryCache && isCacheFresh(memoryCache)) {
    return normalizeProtectedBooksResponse(memoryCache.data);
  }

  if (memoryCache && !isCacheFresh(memoryCache)) {
    booksMemoryCache.delete(cacheKey);
  }

  try {
    const cachedValue =
      window.sessionStorage.getItem(cacheKey) ||
      window.localStorage.getItem(cacheKey);

    if (!cachedValue) {
      return null;
    }

    const parsed = JSON.parse(cachedValue) as CachedProtectedBooksResponse;

    if (!isCacheFresh(parsed)) {
      window.sessionStorage.removeItem(cacheKey);
      window.localStorage.removeItem(cacheKey);
      booksMemoryCache.delete(cacheKey);
      return null;
    }

    const normalized = normalizeProtectedBooksResponse(parsed.data);

    booksMemoryCache.set(cacheKey, {
      saved_at: parsed.saved_at,
      data: normalized,
    });

    return normalized;
  } catch {
    return null;
  }
};

const saveBooksDataToCache = (
  email: string,
  data: ProtectedBooksResponse,
): void => {
  const cleanEmail = normalizeEmail(email);

  if (!cleanEmail) {
    return;
  }

  try {
    const cacheKey = getBooksCacheKey(cleanEmail);

    const cachedValue: CachedProtectedBooksResponse = {
      saved_at: Date.now(),
      data,
    };

    booksMemoryCache.set(cacheKey, cachedValue);
    window.sessionStorage.setItem(cacheKey, JSON.stringify(cachedValue));
    window.localStorage.setItem(cacheKey, JSON.stringify(cachedValue));
  } catch {
    // Cache ist optional.
  }
};

const removeBooksDataFromCache = (email?: string | null): void => {
  const cleanEmail = normalizeEmail(email);

  if (!cleanEmail) {
    return;
  }

  try {
    const cacheKey = getBooksCacheKey(cleanEmail);

    booksMemoryCache.delete(cacheKey);
    booksRequestInFlight.delete(cleanEmail);

    window.sessionStorage.removeItem(cacheKey);
    window.localStorage.removeItem(cacheKey);

    clearOldBookCaches(cleanEmail);
  } catch {
    // Cache löschen ist optional.
  }
};

const fetchProtectedBooks = async (
  email: string,
  options?: {
    force?: boolean;
  },
): Promise<ProtectedBooksResponse> => {
  const cleanEmail = normalizeEmail(email);
  const force = Boolean(options?.force);

  if (!cleanEmail) {
    throw new Error("Ihre E-Mail-Adresse konnte nicht gelesen werden.");
  }

  if (!force) {
    const cachedData = getCachedBooksData(cleanEmail);

    if (cachedData) {
      return cachedData;
    }

    const existingRequest = booksRequestInFlight.get(cleanEmail);

    if (existingRequest) {
      return existingRequest;
    }
  }

  const requestPromise = (async (): Promise<ProtectedBooksResponse> => {
    const params = new URLSearchParams();
    params.set("email", cleanEmail);

    if (force) {
      params.set("force_refresh", "1");
      params.set("_", String(Date.now()));
    }

    const response = await fetch(
      `${API_BASE_URL}/api/protected-books?${params.toString()}`,
      {
        method: "GET",
        cache: "no-store",
        headers: {
          Accept: "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Der Bücher-Zugang konnte nicht geprüft werden. Status: ${response.status}`,
      );
    }

    const json = (await response.json()) as ProtectedBooksResponse;
    const normalized = normalizeProtectedBooksResponse(json);

    saveBooksDataToCache(cleanEmail, normalized);

    return normalized;
  })();

  if (!force) {
    booksRequestInFlight.set(cleanEmail, requestPromise);
  }

  try {
    return await requestPromise;
  } finally {
    booksRequestInFlight.delete(cleanEmail);
  }
};

const mergeBooksWithAccess = (
  booksData: ProtectedBooksResponse | null,
): DisplayBookLevel[] => {
  const unlockedBooksMap = new Map<string, ProtectedBook>();
  const unlockedLevels = new Set<BookLevelKey>();

  if (booksData?.has_full_book_access || booksData?.has_full_access) {
    ALL_BOOK_LEVELS.forEach((level) => unlockedLevels.add(level));
  }

  const accessibleLevels =
    booksData?.accessible_book_levels || booksData?.accessible_levels || [];

  accessibleLevels.forEach((level) => {
    if (isBookLevel(level)) {
      unlockedLevels.add(level);
    }
  });

  booksData?.levels?.forEach((levelGroup) => {
    levelGroup.books.forEach((book) => {
      unlockedBooksMap.set(`${book.level}-${book.book_id}`, book);
    });
  });

  return PUBLIC_BOOK_LEVELS.map((levelGroup) => {
    const books: DisplayBook[] = levelGroup.books.map((publicBook) => {
      const unlockedBook =
        unlockedBooksMap.get(`${publicBook.level}-${publicBook.book_id}`) ||
        null;

      const isUnlocked =
        Boolean(
          booksData?.has_full_book_access || booksData?.has_full_access,
        ) ||
        unlockedLevels.has(publicBook.level) ||
        Boolean(unlockedBook);

      return {
        ...publicBook,
        ...(unlockedBook || {}),
        is_unlocked: isUnlocked,
      };
    });

    return {
      level: levelGroup.level,
      title: levelGroup.title,
      subtitle: levelGroup.subtitle,
      book_count: books.length,
      unlocked_count: books.filter((book) => book.is_unlocked).length,
      books,
    };
  });
};

const buildBookPath = (book: DisplayBook): string => {
  return `/book-view?level=${encodeURIComponent(
    book.level,
  )}&bookId=${encodeURIComponent(
    String(book.book_id),
  )}&book=${encodeURIComponent(book.title)}&file=${encodeURIComponent(
    book.file_name,
  )}`;
};

const renderLevelAccessIcon = (
  isCheckingAccess: boolean,
  isLevelUnlocked: boolean,
): React.ReactNode => {
  if (isCheckingAccess) {
    return (
      <FaSpinner className="book-access-icon book-level-access-icon book-checking-icon" />
    );
  }

  if (isLevelUnlocked) {
    return <FaLockOpen className="book-access-icon book-level-access-icon" />;
  }

  return <FaLock className="book-access-icon book-level-access-icon" />;
};

const renderBookAccessIcon = (
  isCheckingAccess: boolean,
  isBookUnlocked: boolean,
): React.ReactNode => {
  if (isCheckingAccess) {
    return (
      <FaSpinner className="book-access-icon book-item-access-icon book-checking-icon" />
    );
  }

  if (isBookUnlocked) {
    return <FaLockOpen className="book-access-icon book-item-access-icon" />;
  }

  return <FaLock className="book-access-icon book-item-access-icon" />;
};

const BooksPage: React.FC = () => {
  const { isLoggedIn, authLoading, userEmail, userId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const mountedRef = useRef<boolean>(false);
  const lastLoadedKeyRef = useRef<string>("");
  const preloadInFlightRef = useRef<Map<string, Promise<string | null>>>(
    new Map(),
  );

  const cleanEmail = useMemo(() => normalizeEmail(userEmail), [userEmail]);
  const cleanUserId = useMemo(() => normalizeId(userId), [userId]);

  const cleanPathname = useMemo(() => {
    return normalizePathname(location.pathname);
  }, [location.pathname]);

  const isBooksRoute = cleanPathname === "/books";

  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
  const [showSubscriptionMonthly, setShowSubscriptionMonthly] =
    useState<boolean>(false);
  const [pendingBook, setPendingBook] = useState<DisplayBook | null>(null);
  const [openPendingBookAfterLogin, setOpenPendingBookAfterLogin] =
    useState<boolean>(false);
  const [accessStatus, setAccessStatus] = useState<BooksAccessStatus>("idle");
  const [accessError, setAccessError] = useState<string>("");
  const [booksData, setBooksData] = useState<ProtectedBooksResponse | null>(
    null,
  );
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [isOpeningBook, setIsOpeningBook] = useState<boolean>(false);

  const preloadBookHtml = useCallback(
    async (book: DisplayBook): Promise<string | null> => {
      if (!book.is_unlocked) {
        return null;
      }

      const cachedBook = getCachedPreloadedBook(
        cleanEmail,
        book.level,
        book.book_id,
      );

      if (cachedBook?.html) {
        return cachedBook.html;
      }

      const cacheKey = getPreloadedBookCacheKey(
        cleanEmail || "last",
        book.level,
        book.book_id,
      );

      const runningRequest = preloadInFlightRef.current.get(cacheKey);

      if (runningRequest) {
        return runningRequest;
      }

      const requestPromise = (async (): Promise<string | null> => {
        try {
          const params = new URLSearchParams();

          if (cleanEmail) {
            params.set("email", cleanEmail);
          }

          if (cleanUserId) {
            params.set("app_user_id", cleanUserId);
          }

          params.set("force_refresh", "1");
          params.set("book_view", "1");
          params.set("preload", "1");
          params.set("_", String(Date.now()));

          const response = await fetch(
            `${API_BASE_URL}/api/protected-books/${encodeURIComponent(
              book.level,
            )}/${encodeURIComponent(String(book.book_id))}?${params.toString()}`,
            {
              method: "GET",
              cache: "no-store",
              headers: {
                Accept: "text/html,application/json",
                "Cache-Control": "no-cache",
                Pragma: "no-cache",
              },
            },
          );

          if (!response.ok) {
            return null;
          }

          const contentType = response.headers.get("content-type") || "";
          let html = "";

          if (contentType.toLowerCase().includes("application/json")) {
            const json = (await response.json()) as ApiJsonResponse;
            html = getJsonHtml(json);
          } else {
            html = await response.text();
          }

          const cleanHtml = String(html || "").trim();

          if (!cleanHtml) {
            return null;
          }

          savePreloadedBook(cleanEmail, book, cleanHtml);

          return cleanHtml;
        } catch {
          return null;
        } finally {
          preloadInFlightRef.current.delete(cacheKey);
        }
      })();

      preloadInFlightRef.current.set(cacheKey, requestPromise);

      return requestPromise;
    },
    [cleanEmail, cleanUserId],
  );

  const closeBooksModals = useCallback((): void => {
    setShowLoginModal(false);
    setShowSubscriptionMonthly(false);
    setPendingBook(null);
    setOpenPendingBookAfterLogin(false);
  }, []);

  const openBookWithPreload = useCallback(
    async (book: DisplayBook): Promise<void> => {
      const path = buildBookPath(book);
      const fullPath = `${window.location.origin}${path}`;

      closeBooksModals();
      setIsOpeningBook(true);

      const cachedBook = getCachedPreloadedBook(
        cleanEmail,
        book.level,
        book.book_id,
      );

      if (!cachedBook?.html) {
        await Promise.race([
          preloadBookHtml(book),
          new Promise<null>((resolve) => {
            window.setTimeout(() => resolve(null), 320);
          }),
        ]);
      }

      prepareBookViewRoute();

      window.setTimeout(() => {
        window.location.replace(fullPath);
      }, 0);
    },
    [cleanEmail, closeBooksModals, preloadBookHtml],
  );

  const navigateToPath = useCallback(
    (path: string, book?: DisplayBook): void => {
      closeBooksModals();

      if (isBookViewPath(path) && book) {
        void openBookWithPreload(book);
        return;
      }

      navigate(path, {
        replace: false,
      });

      window.requestAnimationFrame(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "auto",
        });
      });
    },
    [closeBooksModals, navigate, openBookWithPreload],
  );

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (isBooksRoute) {
      setIsOpeningBook(false);
    }
  }, [isBooksRoute]);

  useEffect(() => {
    if (!isBooksRoute) {
      closeBooksModals();
      document.documentElement.classList.remove("books-screen-page");
      document.body.classList.remove("books-screen-page");
      return;
    }

    document.documentElement.classList.add("books-screen-page");
    document.body.classList.add("books-screen-page");
    document.documentElement.classList.remove("book-view-screen-page");
    document.body.classList.remove("book-view-screen-page");

    return () => {
      document.documentElement.classList.remove("books-screen-page");
      document.body.classList.remove("books-screen-page");
    };
  }, [closeBooksModals, isBooksRoute]);

  const refreshAccessOnce = useCallback(
    (force = true): void => {
      if (!cleanEmail) {
        return;
      }

      removeBooksDataFromCache(cleanEmail);
      lastLoadedKeyRef.current = "";

      setRefreshKey((current) => current + 1);

      if (force) {
        window.dispatchEvent(
          new CustomEvent("dear-language-books-access-refresh-started"),
        );
      }
    },
    [cleanEmail],
  );

  useEffect(() => {
    const handleExternalRefresh = (): void => {
      refreshAccessOnce(true);
    };

    BOOKS_ACCESS_REFRESH_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleExternalRefresh);
    });

    return () => {
      BOOKS_ACCESS_REFRESH_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleExternalRefresh);
      });
    };
  }, [refreshAccessOnce]);

  useEffect(() => {
    let isCancelled = false;

    const loadBooks = async (): Promise<void> => {
      if (!isBooksRoute) {
        return;
      }

      setAccessError("");

      if (!isLoggedIn) {
        if (!authLoading) {
          setBooksData(null);
          setAccessStatus("ready");
          lastLoadedKeyRef.current = "";
        }

        return;
      }

      if (!cleanEmail) {
        if (!authLoading) {
          setBooksData(null);
          setAccessStatus("error");
          setAccessError(
            "Ihre E-Mail-Adresse konnte nicht gelesen werden. Bitte loggen Sie sich erneut ein.",
          );
        }

        return;
      }

      clearOldBookCaches(cleanEmail);

      const cachedData = getCachedBooksData(cleanEmail);

      if (cachedData) {
        setBooksData(cachedData);
        setAccessStatus("ready");
      }

      if (authLoading) {
        return;
      }

      const loadKey = `${cleanEmail}:${refreshKey}`;

      if (lastLoadedKeyRef.current === loadKey && (cachedData || booksData)) {
        return;
      }

      if (cachedData && refreshKey === 0) {
        lastLoadedKeyRef.current = loadKey;
        return;
      }

      lastLoadedKeyRef.current = loadKey;

      if (!cachedData) {
        setBooksData(null);
        setAccessStatus("checking");
      }

      try {
        const protectedBooks = await fetchProtectedBooks(cleanEmail, {
          force: refreshKey > 0,
        });

        if (!isCancelled && mountedRef.current && isBooksRoute) {
          setBooksData(protectedBooks);
          setAccessStatus("ready");
          setAccessError("");
        }
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Der Bücher-Zugang konnte nicht geprüft werden.";

        if (!isCancelled && mountedRef.current && isBooksRoute) {
          if (cachedData) {
            setBooksData(cachedData);
            setAccessStatus("ready");
            return;
          }

          setAccessStatus("error");
          setAccessError(message);
        }
      }
    };

    void loadBooks();

    return () => {
      isCancelled = true;
    };
  }, [authLoading, booksData, cleanEmail, isBooksRoute, isLoggedIn, refreshKey]);

  const visibleBookLevels = useMemo(() => {
    return mergeBooksWithAccess(booksData);
  }, [booksData]);

  const totalVisibleBooks = useMemo(() => {
    return visibleBookLevels.reduce(
      (total, bookLevel) => total + bookLevel.books.length,
      0,
    );
  }, [visibleBookLevels]);

  const totalUnlockedBooks = useMemo(() => {
    return visibleBookLevels.reduce(
      (total, bookLevel) => total + bookLevel.unlocked_count,
      0,
    );
  }, [visibleBookLevels]);

  const hasFullBookAccess = Boolean(
    booksData?.has_full_book_access || booksData?.has_full_access,
  );

  const isCheckingAccess = accessStatus === "checking";

  useEffect(() => {
    if (
      !isBooksRoute ||
      authLoading ||
      !isLoggedIn ||
      !pendingBook ||
      !openPendingBookAfterLogin ||
      showLoginModal
    ) {
      return;
    }

    if (accessStatus === "idle" || accessStatus === "checking") {
      return;
    }

    const latestPendingBook = visibleBookLevels
      .flatMap((bookLevel) => bookLevel.books)
      .find(
        (book) =>
          book.level === pendingBook.level &&
          book.book_id === pendingBook.book_id,
      );

    if (latestPendingBook?.is_unlocked) {
      setPendingBook(null);
      setOpenPendingBookAfterLogin(false);
      navigateToPath(buildBookPath(latestPendingBook), latestPendingBook);
      return;
    }

    setPendingBook(null);
    setOpenPendingBookAfterLogin(false);
  }, [
    accessStatus,
    authLoading,
    isBooksRoute,
    isLoggedIn,
    navigateToPath,
    openPendingBookAfterLogin,
    pendingBook,
    showLoginModal,
    visibleBookLevels,
  ]);

  useEffect(() => {
    if (!isBooksRoute || !isLoggedIn || accessStatus !== "ready") {
      return;
    }

    const unlockedBooks = visibleBookLevels
      .flatMap((bookLevel) => bookLevel.books)
      .filter((book) => book.is_unlocked);

    let cancelled = false;
    let currentIndex = 0;
    let timeoutId = 0;

    const warmNextBook = (): void => {
      if (cancelled || currentIndex >= unlockedBooks.length) {
        return;
      }

      const nextBook = unlockedBooks[currentIndex];
      currentIndex += 1;

      void preloadBookHtml(nextBook).finally(() => {
        if (!cancelled) {
          timeoutId = window.setTimeout(warmNextBook, 120);
        }
      });
    };

    timeoutId = window.setTimeout(warmNextBook, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    accessStatus,
    isBooksRoute,
    isLoggedIn,
    preloadBookHtml,
    visibleBookLevels,
  ]);

  const headerTitle = hasFullBookAccess
    ? "Alle Deutschbücher"
    : isLoggedIn
      ? "Meine Deutschbücher"
      : "Deutschbücher";

  const headerSubtitle = !isLoggedIn
    ? "Alle Buchtitel sind sichtbar. Zum Öffnen bitte einloggen und den Premium-Zugang abonnieren."
    : hasFullBookAccess
      ? "Vollzugang: Alle Bücher von A1 bis C2 sind freigeschaltet."
      : "Freigeschaltete Bücher können geöffnet werden. Gesperrte Bücher benötigen Premium-Zugang.";

  const handleRefreshAccess = (): void => {
    refreshAccessOnce(true);
  };

  const handleLoginClose = (): void => {
    setShowLoginModal(false);
  };

  const openLoginForBook = (book: DisplayBook): void => {
    setPendingBook(book);
    setShowSubscriptionMonthly(false);
    setOpenPendingBookAfterLogin(true);
    setShowLoginModal(true);
  };

  const openSubscriptionMonthly = (book?: DisplayBook): void => {
    if (book) {
      setPendingBook(book);
    }

    setShowLoginModal(false);
    setOpenPendingBookAfterLogin(false);
    setShowSubscriptionMonthly(true);
  };

  const handleSubscribed = (): void => {
    setShowSubscriptionMonthly(false);
    setPendingBook(null);
    setOpenPendingBookAfterLogin(false);
    refreshAccessOnce(true);
  };

  const warmBookCache = (book: DisplayBook): void => {
    if (!isLoggedIn || !book.is_unlocked || isCheckingAccess || isOpeningBook) {
      return;
    }

    void preloadBookHtml(book);
  };

  const openBook = (book: DisplayBook): void => {
    if (isCheckingAccess || isOpeningBook) {
      return;
    }

    if (!isLoggedIn) {
      openLoginForBook(book);
      return;
    }

    if (!book.is_unlocked) {
      openSubscriptionMonthly(book);
      return;
    }

    navigateToPath(buildBookPath(book), book);
  };

  const renderCardParticles = (): React.ReactNode => {
    return (
      <div className="book-card-particles" aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>
    );
  };

  const renderStatusNotice = (): React.ReactNode => {
    if (authLoading || accessStatus === "checking") {
      return null;
    }

    if (accessStatus !== "error") {
      return null;
    }

    return (
      <article className="book-group book-status-group">
        {renderCardParticles()}

        <div className="book-card-badge book-card-badge-status">
          <section className="book-card-left">
            <div className="book-lock-block">
              <span className="book-level-icon book-level-icon-compact book-level-icon-locked">
                <FaLock className="book-access-icon book-level-access-icon" />
              </span>

              <span className="book-lock-status">Fehler</span>
            </div>

            <div className="book-title-block">
              <h2>!</h2>
              <p>Zugang</p>
            </div>
          </section>

          <section className="book-card-right book-card-right-message">
            <button
              type="button"
              className="book-button book-message-button"
              onClick={handleRefreshAccess}
            >
              <span className="book-name">
                {accessError || "Zugang erneut prüfen"}
              </span>

              <span className="book-arrow book-arrow-compact book-arrow-locked">
                →
              </span>
            </button>
          </section>
        </div>
      </article>
    );
  };

  if (!isBooksRoute || isOpeningBook) {
    return null;
  }

  return (
    <div className="books-page">
      <Login isOpen={showLoginModal} onClose={handleLoginClose} />

      <SubscriptionMonthly
        isOpen={showSubscriptionMonthly}
        onClose={() => {
          setShowSubscriptionMonthly(false);
          setPendingBook(null);
          setOpenPendingBookAfterLogin(false);
        }}
        onSubscribed={handleSubscribed}
      />

      <section className="books-container">
        <header className="books-header-card">
          <div className="books-header-content">
            <p className="books-kicker">Deutschbibliothek</p>
            <h1 className="books-page-title">{headerTitle}</h1>
            <p className="books-subtitle">{headerSubtitle}</p>
          </div>

          <div className="books-header-badge">
            <strong>
              {isCheckingAccess
                ? "..."
                : `${totalUnlockedBooks}/${totalVisibleBooks}`}
            </strong>
            <span>Freigeschaltete Bücher</span>
          </div>
        </header>

        {renderStatusNotice()}

        <div className="books-learning-grid">
          {visibleBookLevels.map(({ level, title, books, unlocked_count }) => {
            const isLevelUnlocked = unlocked_count > 0;

            const cardStatusClassName = isCheckingAccess
              ? "book-card-checking"
              : isLevelUnlocked
                ? "book-card-unlocked"
                : "book-card-locked";

            const levelIconClassName = isCheckingAccess
              ? "book-level-icon-checking"
              : isLevelUnlocked
                ? "book-level-icon-unlocked"
                : "book-level-icon-locked";

            const statusText = isCheckingAccess
              ? "Prüfen"
              : isLevelUnlocked
                ? "Freigeschaltet"
                : "Gesperrt";

            const countText = isCheckingAccess
              ? "Prüfung läuft"
              : `${unlocked_count}/${books.length} freigeschaltet`;

            return (
              <article
                key={level}
                className={`book-group book-${level.toLowerCase()} ${cardStatusClassName}`}
              >
                {renderCardParticles()}

                <div className="book-card-badge">
                  <section className="book-card-left">
                    <div className="book-lock-block">
                      <span
                        className={`book-level-icon book-level-icon-compact ${levelIconClassName}`}
                      >
                        {renderLevelAccessIcon(
                          isCheckingAccess,
                          isLevelUnlocked,
                        )}
                      </span>

                      <span className="book-lock-status">{statusText}</span>
                    </div>

                    <div className="book-title-block">
                      <h2>{level}</h2>
                      <p>{title}</p>
                      <small>{countText}</small>
                    </div>
                  </section>

                  <section className="book-card-right">
                    <ul className="book-list">
                      {books.map((book) => {
                        const arrowClassName = isCheckingAccess
                          ? "book-arrow-checking"
                          : book.is_unlocked
                            ? "book-arrow-unlocked"
                            : "book-arrow-locked";

                        return (
                          <li
                            key={`${book.level}-${book.book_id}`}
                            className="book-item"
                          >
                            <button
                              type="button"
                              className={`book-button ${
                                book.is_unlocked ? "" : "book-button-locked"
                              }`}
                              onPointerEnter={() => warmBookCache(book)}
                              onMouseEnter={() => warmBookCache(book)}
                              onFocus={() => warmBookCache(book)}
                              onTouchStart={() => warmBookCache(book)}
                              onClick={() => openBook(book)}
                              disabled={isOpeningBook}
                              aria-label={`${book.title} öffnen`}
                            >
                              <span className="book-name">{book.title}</span>

                              <span
                                className={`book-arrow book-arrow-compact ${arrowClassName}`}
                              >
                                {renderBookAccessIcon(
                                  isCheckingAccess,
                                  book.is_unlocked,
                                )}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default BooksPage;